import { createFileRoute, redirect } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { FEATURES, isFeatureEnabled } from '@/utils/feature-flag.ts'
import { useUser } from '@/features/user/hooks'
import { UserCard, type UserCardActions } from '@/features/user/components/UserCard.tsx'
import { useCompany, useAuth } from '@/contexts'
import { QueryLoader } from '@/components/QueryLoader/QueryLoader.tsx'
import {
  useSuspendEmployee,
  useResumeEmployee
} from '@/features/user/hooks/useAssignEmployeeActivationState.ts'
import { useInvitableRoles } from '@/features/user/hooks'
import { useMessageSnackbar } from '@/hooks/useMessageSnackbar.ts'
import { useErrorMessageTranslation } from '@/hooks/useErrorMessageTranslation.ts'
import { ConfirmDialog } from '@/components/Dialog/ConfirmDialog.tsx'
import React from 'react'
import type { UserEmployee } from '@/features/user/api/user-employee.data.ts'
import { queryClient } from '@/utils/query-client.ts'
import { userQueryOptions } from '@/features/user/hooks/useUser.ts'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import BorderColorTwoToneIcon from '@mui/icons-material/BorderColorTwoTone'
import type { MenuItemListElement } from '@/components/Menu/MenuItemList.tsx'

export const Route = createFileRoute(
  '/_authenticated/$companySlug/users/_overview/$userId'
)({
  beforeLoad: ({ context }) => {
    if (!isFeatureEnabled(FEATURES.USER_MANAGEMENT)) {
      throw redirect({
        to: '/not-found'
      })
    }
    context.auth.rejectIfMissingPermissions(['USER_READ'])
  },
  loader: ({ params: { userId } }) =>
    queryClient.ensureQueryData(userQueryOptions(userId)),
  component: UserDetail
})

function UserDetail() {
  const { t } = useTranslation(['@feature_user', '_global'])
  const { userId } = Route.useParams()
  const navigate = Route.useNavigate()
  const company = useCompany()
  const auth = useAuth()
  const { showSuccess, showError } = useMessageSnackbar()
  const { translateError } = useErrorMessageTranslation()

  const userQuery = useUser({ userId })
  const writableRoles = useInvitableRoles()
  const suspendEmployee = useSuspendEmployee()
  const resumeEmployee = useResumeEmployee()

  const [dialogAction, setDialogAction] = React.useState<'suspend' | 'resume' | null>(
    null
  )

  const handleClose = () => {
    navigate({
      to: '/$companySlug/users'
    })
  }

  const canEditUser = (user: UserEmployee) => {
    return (
      auth.status == 'AUTHENTICATED' &&
      auth.hasPermissions(['USER_WRITE']) &&
      company.access === 'COMPANY_WIDE' &&
      writableRoles.data &&
      writableRoles.data.roles.includes(user.role)
    )
  }

  const canChangeEmployeeActivationState = (user: UserEmployee) => {
    return canEditUser(user) && company.access == 'COMPANY_WIDE' && user.role != 'ADMIN'
  }

  const confirmSuspend = () => {
    setDialogAction(null)
    suspendEmployee.mutateAsync(
      { userId },
      {
        onSuccess: () => {
          showSuccess(t('components.UserList.actions.suspend.success'), {
            title: t('components.UserList.actions.suspend.buttonText')
          })
          handleClose()
        },
        onError: error =>
          showError(
            t('components.UserList.actions.suspend.error', {
              message: translateError(error)
            }),
            { title: t('components.UserList.actions.suspend.buttonText') }
          )
      }
    )
  }

  const confirmResume = () => {
    setDialogAction(null)
    resumeEmployee.mutateAsync(
      { userId },
      {
        onSuccess: () => {
          showSuccess(t('components.UserList.actions.resume.success'), {
            title: t('components.UserList.actions.resume.buttonText')
          })
          handleClose()
        },
        onError: error =>
          showError(
            t('components.UserList.actions.resume.error', {
              message: translateError(error)
            }),
            { title: t('components.UserList.actions.resume.buttonText') }
          )
      }
    )
  }

  const handleUserUpdate = (user: UserEmployee) => {
    // Navigate to the user update route
    navigate({
      to: '/$companySlug/users/$userId/edit',
      params: { userId: user.id }
    })
  }

  const drawerMenuItems = (user: UserEmployee): MenuItemListElement[] =>
    [
      canEditUser(user) && {
        label: t('components.UserList.actions.edit'),
        icon: BorderColorTwoToneIcon,
        onClick: () => handleUserUpdate(user)
      },
      {
        label: t('components.UserList.actions.delete'),
        icon: DeleteOutlineOutlinedIcon
      }
    ].filter(it => !!it)

  const getUserActions = (user: UserEmployee): UserCardActions[] => {
    if (user.isSuspended) {
      return [
        {
          label: t('components.UserList.actions.resume.buttonText'),
          color: 'success',
          onClick: () => setDialogAction('resume'),
          disable: () => !canChangeEmployeeActivationState(user)
        }
      ]
    } else {
      return [
        {
          label: t('components.UserList.actions.suspend.buttonText'),
          color: 'error',
          onClick: () => setDialogAction('suspend'),
          disable: () => !canChangeEmployeeActivationState(user)
        }
      ]
    }
  }

  return (
    <>
      <QueryLoader query={userQuery}>
        {user => (
          <UserCard
            user={user}
            sites={company.sites}
            actions={getUserActions(user)}
            topMenuActions={{ items: drawerMenuItems(user) }}
            onClose={handleClose}
          />
        )}
      </QueryLoader>

      <ConfirmDialog
        open={dialogAction === 'suspend'}
        variant="warning"
        title={t('components.UserList.actions.suspend.dialog.title')}
        onClose={() => setDialogAction(null)}
        actions={[
          {
            label: 'Cancel',
            variant: 'text',
            onClick: () => setDialogAction(null)
          },
          {
            label: t('components.UserList.actions.suspend.buttonText'),
            variant: 'contained',
            onClick: confirmSuspend
          }
        ]}
      >
        {t('components.UserList.actions.suspend.dialog.subtitle')}
      </ConfirmDialog>

      <ConfirmDialog
        open={dialogAction === 'resume'}
        variant="warning"
        title={t('components.UserList.actions.resume.dialog.title')}
        onClose={() => setDialogAction(null)}
        actions={[
          {
            label: 'Cancel',
            variant: 'text',
            onClick: () => setDialogAction(null)
          },
          {
            label: t('components.UserList.actions.resume.buttonText'),
            variant: 'contained',
            onClick: confirmResume
          }
        ]}
      >
        {t('components.UserList.actions.resume.dialog.subtitle')}
      </ConfirmDialog>
    </>
  )
}
